import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { useClubMembers, useRemoveClubMember, useSetClubMemberRole } from '@/hooks/clubs'
import type { ClubMember } from '@/types'
import { MemberAvatar } from './MemberAvatar'

/** Member list; admins can promote/demote and remove */
export function ClubMembersCard({ clubId, isAdmin }: { clubId: string; isAdmin: boolean }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: members = [], isLoading } = useClubMembers(clubId)
  const setRole = useSetClubMemberRole()
  const removeMember = useRemoveClubMember()
  const [toRemove, setToRemove] = useState<ClubMember | null>(null)

  return (
    <Card className="border-0 shadow-sm min-w-0">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          👥 {t('clubs.members.title', { count: members.length })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-20 animate-pulse rounded bg-muted" />
        ) : (
          <ul className="space-y-3">
            {members.map((member) => (
              <li key={member.user_id} className="flex items-center gap-3">
                <MemberAvatar
                  userId={member.user_id}
                  name={member.display_name}
                  avatarUrl={member.avatar_url}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {member.display_name}
                    {member.user_id === user?.id && (
                      <span className="text-muted-foreground"> ({t('clubs.members.you')})</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {member.role === 'admin' ? t('clubs.roles.admin') : t('clubs.roles.member')}
                  </p>
                </div>
                {isAdmin && member.user_id !== user?.id && (
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={setRole.isPending}
                      onClick={() =>
                        setRole.mutate({
                          clubId,
                          userId: member.user_id,
                          role: member.role === 'admin' ? 'member' : 'admin',
                        })
                      }
                    >
                      {member.role === 'admin'
                        ? t('clubs.members.makeMember')
                        : t('clubs.members.makeAdmin')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-error"
                      onClick={() => setToRemove(member)}
                    >
                      {t('clubs.members.remove')}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <ConfirmDialog
        isOpen={!!toRemove}
        title={t('clubs.members.removeTitle')}
        message={t('clubs.members.removeMessage', { name: toRemove?.display_name })}
        confirmText={t('clubs.members.remove')}
        variant="danger"
        onCancel={() => setToRemove(null)}
        onConfirm={async () => {
          if (toRemove) await removeMember.mutateAsync({ clubId, userId: toRemove.user_id })
          setToRemove(null)
        }}
      />
    </Card>
  )
}
